/**
 * Runtime endpoint configuration.
 *
 * This is the single place that derives API / WebSocket / tile-server hosts
 * from `VITE_*` env vars. When a variable is unset the app falls back to the
 * page's own origin (`${window.location.origin}/api`, …) so self-hosted /
 * on-prem deployments behind a reverse proxy work without baking a vendor
 * host into the bundle.
 *
 * Values are read lazily (getter functions) so SSR, tests (`vi.stubEnv`) and
 * the browser all see the correct environment.
 */

const DEFAULT_PAGE_ORIGIN = "http://localhost";
const DEFAULT_TRUSTED_DOWNLOAD_HOSTS = ["rivmo-prod.online", "romdao.com"];
const DEFAULT_TENANT_ROOT_DOMAIN = "romdao.com";

type RuntimeEnvKey =
  | "VITE_API_URL"
  | "VITE_WS_URL"
  | "VITE_TILESERVER_URL"
  | "VITE_PG_TILESERVER_URL"
  | "VITE_TRUSTED_DOWNLOAD_HOSTS"
  | "VITE_TENANT_ROOT_DOMAIN"
  | "VITE_TENANT_SLUG";

const readEnv = (key: RuntimeEnvKey): string => {
  const env = (import.meta.env ?? {}) as Record<string, unknown>;
  const value = env[key];
  return typeof value === "string" ? value.trim() : "";
};

const stripTrailingSlashes = (value: string): string =>
  value.replace(/\/+$/, "");

const toOrigin = (value: string, base: string): string => {
  try {
    return new URL(value, base).origin;
  } catch {
    return base;
  }
};

const splitList = (value: string): string[] =>
  value
    .split(",")
    .map((entry) => entry.trim().toLowerCase().replace(/^\.+/, ""))
    .filter(Boolean);

/** Origin of the page currently running the SPA (or a safe SSR default). */
export const getPageOrigin = (): string => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return DEFAULT_PAGE_ORIGIN;
};

/** REST API base URL (no trailing slash), e.g. `https://api.example.com/api`. */
export const getApiUrl = (): string =>
  stripTrailingSlashes(readEnv("VITE_API_URL")) || `${getPageOrigin()}/api`;

/** Origin of the REST API, e.g. `https://api.example.com`. */
export const getApiOrigin = (): string =>
  toOrigin(getApiUrl(), getPageOrigin());

/**
 * WebSocket base. Only the origin is used when building socket URLs; http(s)
 * schemes are converted to ws(s) by `buildAuthenticatedWebSocketConfig`.
 */
export const getWsUrl = (): string =>
  stripTrailingSlashes(readEnv("VITE_WS_URL")) || getApiUrl();

/** Martin / vector tile server base URL. */
export const getTileserverUrl = (): string =>
  stripTrailingSlashes(readEnv("VITE_TILESERVER_URL")) ||
  `${getApiOrigin()}/tiles`;

/** pg_tileserv base URL. */
export const getPgTileserverUrl = (): string =>
  stripTrailingSlashes(readEnv("VITE_PG_TILESERVER_URL")) ||
  `${getApiOrigin()}/pg-tiles`;

/**
 * Domains (matched exactly or as a dot-suffix) whose download URLs may
 * receive the session token. Configured via comma-separated
 * `VITE_TRUSTED_DOWNLOAD_HOSTS`.
 */
export const getTrustedDownloadHosts = (): string[] => {
  const configured = readEnv("VITE_TRUSTED_DOWNLOAD_HOSTS");
  return configured
    ? splitList(configured)
    : [...DEFAULT_TRUSTED_DOWNLOAD_HOSTS];
};

/** Root domain used for whitelabel tenant subdomains (`acme.<root>`). */
export const getTenantRootDomain = (): string =>
  readEnv("VITE_TENANT_ROOT_DOMAIN")
    .toLowerCase()
    .replace(/^\.+|\.+$/g, "") || DEFAULT_TENANT_ROOT_DOMAIN;

/** Forced tenant slug for single-tenant custom domains (empty when unset). */
export const getTenantSlugOverride = (): string =>
  readEnv("VITE_TENANT_SLUG").toLowerCase();

/* Convenience snapshots evaluated at module load (browser bundle). Prefer the
 * getters above in code that may run during SSR or in tests. */
export const API_URL = getApiUrl();
export const API_ORIGIN = getApiOrigin();
export const WS_URL = getWsUrl();
export const TILESERVER_URL = getTileserverUrl();
export const PG_TILESERVER_URL = getPgTileserverUrl();
